import { useDispatch, useSelector } from 'react-redux';
import { useRequest } from 'ahooks';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LoadingFullScreen from '@components/atoms/Loading/LoadingFullScreen';
import { useOktaAuth } from './OktaContext';
import {
  setTokenLogin,
  getRolesAfterActiveProfile,
  getAgentInfo,
  checkIsHousehold,
} from '@stores/auth/actions';
import { RoutePaths } from '@core/routes/routeConfig';
import { selectLoading } from '@stores/global/selector';
import { selectAgentInfo, selectToken } from '@stores/auth/selectors';
import { sessionStorageUtils } from '@helpers/sessionStorageUtils.js';
import { useIdleTimer } from 'react-idle-timer';
import { logOutService } from '@services/user';
import { ConfirmationModal } from '@components/molecules/PopupModal/ConfirmationModal.jsx';
import { loadingRequest, loadingSuccess } from '@stores/global/actions';
import { getCookie, deleteCookie } from '@helpers/cookie';

const CHECKTANDC = 'checkTAndC';

const SecureRoute = ({ component: Component }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const isLoading = useSelector(selectLoading);
  const token = useSelector(selectToken);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isSyncProfile, setIsSyncProfile] = useState(true);
  const [
    isOpenNotificationTokenExpiredDialog,
    setIsOpenNotificationTokenExpiredDialog,
  ] = React.useState(false);
  const agentInfo = useSelector(selectAgentInfo);

  useRequest(async () => {
    if (oktaAuth) {
      const auth = await oktaAuth.authStateManager.getAuthState();
      if (
        auth?.isAuthenticated &&
        sessionStorageUtils.getItem('_profiles')?.profiles?.length > 0
      ) {
        dispatch(getRolesAfterActiveProfile());
        dispatch(checkIsHousehold());
        const renewToken = await oktaAuth?.token?.renewTokens();
        return renewToken;
      }
    }
  });

  useEffect(() => {
    if (!oktaAuth) {
      return;
    }

    if (
      authState?.isAuthenticated &&
      sessionStorageUtils.getItem('_profiles')?.profiles?.length > 0
    ) {
      oktaAuth.token.getWithoutPrompt().then(({ tokens }) => {
        oktaAuth.tokenManager.setTokens(tokens);
        oktaAuth.authStateManager.updateAuthState();
        dispatch(setTokenLogin(tokens.accessToken.accessToken));
        dispatch(getAgentInfo());
      });
    }
  }, [oktaAuth, authState?.isAuthenticated]);

  /**
   * Checking user is authenticated
   * if(isAuthenticated) call api get agent profile
   * else redirect to /login
   */

  useEffect(() => {
    const handleLogin = async () => {
      if (!authState?.isAuthenticated && !token) {
        return navigate(RoutePaths.LOGIN, { replace: true });
      }
    };
    handleLogin();
  }, [authState, navigate, token, dispatch]);

  useEffect(() => {
    const agentCode = agentInfo?.agentCode;
    const sessionAgtCd = sessionStorageUtils.getItem('_agentcod');
    if (sessionAgtCd && agentCode) {
      setIsSyncProfile(sessionAgtCd.toString() === agentCode.toString());
    } else {
      setIsSyncProfile(true);
    }
  }, [agentInfo?.agentCode]);

  const onShowNotificationTokenExpiredDialog = () => {
    if (!isOpenNotificationTokenExpiredDialog) {
      setIsOpenNotificationTokenExpiredDialog(true);
    }
  };

  const onCloseNotificationTokenExpiredDialog = (action) => {
    setIsOpenNotificationTokenExpiredDialog(false);
    if (action === 'agree') {
      onLogout();
    }
  };

  const onLogout = () => {
    setIsOpenNotificationTokenExpiredDialog(false);
    dispatch(loadingRequest());
    logOutService().then((res) => {
      dispatch(loadingSuccess());
      if (res?.message?.includes('Successful')) {
        sessionStorageUtils.removeItem('_profiles');
        sessionStorageUtils.removeItem('checkTAndC');
        if (getCookie(CHECKTANDC)) {
          deleteCookie(CHECKTANDC);
        }
      }
      oktaAuth.signOut();
    });
  };

  useIdleTimer({
    onIdle: onShowNotificationTokenExpiredDialog,
    timeout: 300_000,
    throttle: 500,
  });

  useIdleTimer({
    onIdle: onLogout,
    timeout: 1080_000,
    throttle: 500,
  });

  if (isLoading || !isSyncProfile) {
    return <LoadingFullScreen loading={true} />;
  }

  return (
    <>
      <Component />
      <ConfirmationModal
        open={isOpenNotificationTokenExpiredDialog}
        title={'Hết phiên đăng nhập'}
        content={
          'Khi không được sử dụng, hệ thống sẽ tự động đăng xuất. Anh/Chị có cần thêm thời gian ?'
        }
        cancelButtonTitle={'Tiếp tục sử dụng'}
        agreeButtonTitle={'Đăng xuất'}
        onClose={onCloseNotificationTokenExpiredDialog}
      />
    </>
  );
};
export default React.memo(SecureRoute);

SecureRoute.propTypes = {
  component: PropTypes.any,
};
